// Footmen Wars bots (2026-10-25). They play through Sim.command like a person would, at a staggered think rate (Sheep
// Tag's ai.ts pattern), and read only what a player could see on the field. Plan per think:
// 1. Tier up the moment the gate is open and the gold is there (the first one picks an army).
// 2. Set the slots to counter what the enemy is fielding near the bot's own army (ROSTER.md: "bots read the enemy mix
//    near the front and pick the counter"), keeping one Line slot as the front.
// 3. Choose a goal: defend if the base is under attack, push the weakest neighbour once the army is big and bases are
//    open, else hold the center.
// 4. The hero (2026-10-26): picked on the first think, both skills cast the moment they're up (the sim refuses a cast
//    with nothing in reach), and at the center a salve when hurt, a whetstone with gold left after the next tier (the
//    army comes first: a bot that bought salves before its tier never picked an army, harness 2026-10-26). Once the
//    ultimate is learned and ready, the first skill waits until the mana for both is there: Liang's Signal at 40 mana
//    every 14 s kept her under 100 for whole matches and the River Drums never went (harness 2026-10-26).
// 5. The center's giant (2026-10-26): while it stands (or is about to wake) a bot goes for it only when its army beats
//    the giant plus every enemy soldier near the middle by BOSS.margin, else it waits at its own camp, out of the
//    giant's reach. Pushing a base and defending home still come first and second.
// 6. Extras (2026-10-26): an army with a ram buys one each time it can while it is pushing an enemy base, with gold left
//    after the next tier (and before the hero's shop, so whetstones don't eat the push). The hero plays after the goal.
import { hypot } from './dmath';
import { BASE, BOSS, BUDGET_DPS, BUDGET_HP, CLASS, CLASSES, ClassId, EXTRA, FACTION, FACTIONS, FactionId, HERO, HEROES, HeroId, ITEM, TIER_COST } from './defs';
import { PLAYERS, STATE, Sim } from './sim';
import { arrivalRadius } from './order-rules';

/** A bot takes on the giant when its army's strength (total HP times total damage a second, the square law) is this many
 *  times the giant's plus the enemy soldiers' near the middle. Seconds before a wave that bots start lining up. */
const BOSS_LEAD = 15, MIDDLE = 20;
/** The next giant's growth, while it still sleeps. */
const nextGrow = (s: Sim) => 1 + BOSS.growth * s.bossWave;

/** Score added to a target base for each other player already pushing it, so bots spread out instead of all three
 *  piling on the weakest and fighting each other at its gate (2026-10-26: a third of attackers at a base were fighting
 *  another attacker). About 7 enemy soldiers' worth in the score below. Only an army of PILE_ON_ARMY or more counts, so
 *  an empty army's order can't steer the bots (Astra, 2026-10-26). */
const PILE_ON = 200, PILE_ON_ARMY = 12;

/** The class that beats each class (ROSTER.md section 2's counter loop). */
const ANSWER: Record<ClassId, ClassId[]> = {
  footman: ['line'], line: ['siege', 'ranged'], spear: ['ranged', 'line'], ranged: ['cavalry', 'line'],
  cavalry: ['spear'], siege: ['cavalry'], support: ['ranged', 'cavalry'], elite: [], hero: [], ram: ['cavalry'],
};

export class Bot {
  private t = 0;
  constructor(readonly sim: Sim, readonly p: number, readonly faction: FactionId = FACTIONS[(p * 3 + sim.rng.int(0, 7)) % FACTIONS.length], readonly every = 1.5,
    readonly hero: HeroId = HEROES[(p * 3 + sim.rng.int(0, HEROES.length - 1)) % HEROES.length]) {
    this.t = (p * every) / PLAYERS;
  }

  tick(dt: number) {
    this.t -= dt;
    if (this.t > 0) return;
    this.t += this.every;
    const s = this.sim, pl = s.players[this.p];
    if (pl.out) return;
    if (s.canTier(this.p)) s.command(this.p, { t: 'tier', faction: this.faction });

    // what the enemy fields near my army (within 30 of my units' middle), else anywhere
    let mx = 0, my = 0, n = 0;
    for (let i = 0; i < s.x.length; i++) if (s.state[i] !== STATE.dead && s.team[i] === this.p) { mx += s.x[i]; my += s.y[i]; n++; }
    if (n) { mx /= n; my /= n; } else { mx = s.bases[this.p].x; my = s.bases[this.p].y; }
    const seen: Record<string, number> = {};
    for (let i = 0; i < s.x.length; i++) {
      if (s.state[i] === STATE.dead || s.team[i] === this.p) continue;
      if (hypot(s.x[i] - mx, s.y[i] - my) > 30) continue;
      const c = CLASSES[s.cls[i]]; seen[c] = (seen[c] ?? 0) + 1;
    }
    if (pl.tier >= 1) this.pickSlots(seen);

    // goal
    const b = s.bases[this.p];
    let threat = 0;
    for (let i = 0; i < s.x.length; i++) if (s.state[i] !== STATE.dead && s.team[i] !== this.p && hypot(s.x[i] - b.x, s.y[i] - b.y) < 16) threat++;
    let goal = pl.goal;
    const giant = s.boss >= 0 || s.nextBossAt() - s.time < BOSS_LEAD;
    if (threat >= 4 && s.time >= BASE.safeUntil - 20) goal = { kind: 'base', p: this.p };
    else if (giant && this.strongEnough()) goal = { kind: 'center' };
    else if (s.time >= BASE.safeUntil && pl.alive >= 22) {
      let best = -1, score = Infinity;
      for (let q = 0; q < PLAYERS; q++) {
        if (q === this.p || s.players[q].out) continue;
        const o = s.players[q], sc = o.alive * 30 + o.baseHp / 50 + (Math.abs(q - this.p) === 2 ? 60 : 0) // the far corner costs more to reach
          + PILE_ON * s.players.filter((r, k) => k !== this.p && k !== q && !r.out && r.alive >= PILE_ON_ARMY && r.goal.kind === 'base' && r.goal.p === q).length;
        if (sc < score) { score = sc; best = q; }
      }
      goal = best >= 0 ? { kind: 'base', p: best } : { kind: 'center' };
    } else if (giant && (pl.goal.kind === 'center' || pl.alive < 12)) {
      // fall back along the road past the camp by the crowd's own settling radius, so the whole army stops outside the
      // Giant's reach (2026-10-29, Flash: settling at the camp itself left the back ranks inside it)
      const c = s.campSpot(this.p), dx = c.x - s.center.x, dy = c.y - s.center.y, d = hypot(dx, dy), out = d + arrivalRadius(pl.alive, 0.8) + 1;
      goal = { kind: 'point', x: s.center.x + dx / d * out, y: s.center.y + dy / d * out };
    }
    else if (!giant && pl.goal.kind === 'point') goal = { kind: 'center' };
    else if (pl.alive < 12) goal = { kind: 'center' };
    if (JSON.stringify(goal) !== JSON.stringify(pl.goal)) s.command(this.p, { t: 'goal', goal });
    // the ram on this think's goal, not the last one's (it was bought on the way out of a push it then gave up: Astra
    // 2026-10-27 F1), and before the hero's shop
    const reserve = pl.tier < 4 ? TIER_COST[pl.tier] : 0;
    if (goal.kind === 'base' && goal.p !== this.p && pl.gold - reserve >= EXTRA.cost && s.canExtra(this.p)) s.command(this.p, { t: 'extra' });
    this.playHero();
  }

  /** Whether my army can take the giant and whoever else is in the middle (what a player sees: their soldiers there). */
  private strongEnough() {
    const s = this.sim, c = s.center;
    let hp = 0, dps = 0, fhp = 0, fdps = 0;
    for (let i = 0; i < s.x.length; i++) {
      if (s.state[i] === STATE.dead) continue;
      const v = s.dmg[i] / s.rate[i];
      if (s.team[i] === this.p) { hp += s.hp[i]; dps += v; }
      else if (s.team[i] < PLAYERS && hypot(s.x[i] - c.x, s.y[i] - c.y) < MIDDLE) { fhp += s.hp[i]; fdps += v; }
    }
    // the giant's slam lands on its target and half on everyone near it: count it about twice; a giant still asleep at full
    const top = Math.max(1, ...s.players.map((q) => q.tier)), g = s.boss;
    const ghp = g >= 0 ? s.hp[g] : BOSS.hp * nextGrow(s) * (BUDGET_HP[top] / BUDGET_HP[1]);
    const gdps = (g >= 0 ? s.dmg[g] / s.rate[g] : (BOSS.dmg * nextGrow(s) * (BUDGET_DPS[top] / BUDGET_DPS[1])) / BOSS.cd) * 2;
    return hp * dps >= BOSS.margin * (ghp + fhp) * (gdps + fdps);
  }

  private playHero() {
    const s = this.sim, pl = s.players[this.p];
    if (!pl.hero) { s.command(this.p, { t: 'hero', id: this.hero }); return; }
    const h = pl.hero;
    if (h.i < 0) return;
    const [first, ult] = HERO[h.id].skills;
    s.command(this.p, { t: 'cast', k: 1 });
    if (h.level < ult.level || h.ready[1] > s.time || h.mana >= first.mana + ult.mana) s.command(this.p, { t: 'cast', k: 0 });
    if (!s.nearCenter(h.i)) return;
    const reserve = pl.tier < 4 ? TIER_COST[pl.tier] : 0;
    if (s.outOfCombat(h.i) && s.salveT[h.i] <= 0 && s.hp[h.i] < s.maxHp[h.i] * 0.5 && pl.gold - reserve >= ITEM.salve.cost(0)) s.command(this.p, { t: 'buy', item: 'salve' });
    if (pl.gold - reserve >= ITEM.whetstone.cost(h.whet)) s.command(this.p, { t: 'buy', item: 'whetstone' });
  }

  private pickSlots(seen: Record<string, number>) {
    const s = this.sim, pl = s.players[this.p], f = FACTION[pl.faction!];
    const open = (c: ClassId) => CLASS[c].tier <= pl.tier && c !== 'footman';
    const ranked = Object.entries(seen).sort((a, b) => b[1] - a[1]).map(([c]) => c as ClassId);
    const want: ClassId[] = ['line'];
    for (const foe of ranked) {
      const answers = foe === 'elite' ? f.eliteCounters : ANSWER[foe];
      for (const a of answers) if (open(a) && want.filter((w) => w === a).length < CLASS[a].maxSlots) { want.push(a); break; }
      if (want.length >= pl.slots.length) break;
    }
    // fill the rest with the tier's best all-rounders
    for (const c of ['elite', 'siege', 'cavalry', 'ranged', 'support', 'spear', 'line'] as ClassId[]) {
      while (want.length < pl.slots.length && open(c) && want.filter((w) => w === c).length < CLASS[c].maxSlots) want.push(c);
    }
    want.length = pl.slots.length;
    // change only slots whose class is not already wanted somewhere, so slots don't churn
    const have = [...pl.slots];
    const missing = want.filter((c) => { const k = have.indexOf(c); if (k >= 0) { have[k] = '' as ClassId; return false; } return true; });
    for (let k = 0; k < pl.slots.length && missing.length; k++) {
      const c = pl.slots[k];
      if (want.filter((w) => w === c).length >= pl.slots.filter((x, j) => j <= k && x === c).length) continue;
      if (s.command(this.p, { t: 'slot', i: k, cls: missing[0] })) missing.shift();
    }
  }
}
